/**
 * First-run onboarding wizard — the guided 8-step setup that kills the
 * empty-CRM cold start (forge pattern: step pills across the top, a
 * progress bar, one rich surface per step, review-then-activate).
 *
 * GATING: `<FirstRunWizard />` mounts at the app shell and renders a
 * full-screen overlay ONLY when the store has zero accounts AND zero
 * people AND the onboarding flag is unset (pure logic:
 * `shouldShowOnboarding` in src/lib/onboarding.ts). Once shown it LATCHES
 * open — activation writes data and sets the flag, which would otherwise
 * yank the overlay away mid-celebration — and only an explicit close
 * (Enter Argo OS / Skip for now) dismisses it. Skip sets the flag too:
 * the user is never trapped.
 *
 * All writes here are direct founder input (typed into this form), so
 * they go straight to the store — the approval queue governs AGENT
 * writes, and no agent is involved. Deterministic; no model calls.
 */

import { useEffect, useMemo, useState, type KeyboardEvent } from 'react';
import { GlassPanel } from '../../components/GlassPanel';
import { db } from '../../lib/db/store';
import {
  ONBOARDING_STEPS,
  activateOnboarding,
  emptyDraft,
  markOnboardingCompleted,
  shouldShowOnboarding,
  subscribeOnboardingCompleted,
  validateStep,
  type ActivationResult,
  type OnboardingDraft,
} from '../../lib/onboarding';
import { StepCompany } from './steps/StepCompany';
import { StepModules } from './steps/StepModules';
import { StepQuality } from './steps/StepQuality';
import { StepPersonas } from './steps/StepPersonas';
import { StepTeam } from './steps/StepTeam';
import { StepGoals } from './steps/StepGoals';
import { StepReview } from './steps/StepReview';
import { StepActivate } from './steps/StepActivate';

// ---------------------------------------------------------------------------
// Gate — subscribes to the live store and latches the wizard open
// ---------------------------------------------------------------------------

export function FirstRunWizard() {
  const [accounts, setAccounts] = useState<number | null>(null);
  const [people, setPeople] = useState<number | null>(null);
  const [completed, setCompleted] = useState<boolean | null>(null);
  const [active, setActive] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const offAccounts = db.subscribe('accounts', (rs) => setAccounts(rs.length));
    const offPeople = db.subscribe('people', (rs) => setPeople(rs.length));
    const offFlag = subscribeOnboardingCompleted(setCompleted);
    return () => {
      offAccounts();
      offPeople();
      offFlag();
    };
  }, []);

  // Dev-only preview escape hatch: the local demo seeder repopulates the
  // store on boot, so the cold-start gate can never open in dev. `?onboarding=preview`
  // forces the overlay for visual QA; import.meta.env.DEV compiles it out of prod.
  const forcePreview =
    import.meta.env.DEV &&
    new URLSearchParams(window.location.search).get('onboarding') === 'preview';

  // Latch: once the cold-start condition is observed, stay open until an
  // explicit close — activation's own writes must not dismiss the overlay.
  useEffect(() => {
    if (active || dismissed) return;
    if (forcePreview) {
      setActive(true);
      return;
    }
    if (accounts === null || people === null || completed === null) return;
    if (shouldShowOnboarding({ accounts, people }, completed)) setActive(true);
  }, [accounts, people, completed, active, dismissed, forcePreview]);

  if (!active || dismissed) return null;
  return <OnboardingWizard onClose={() => setDismissed(true)} />;
}

// ---------------------------------------------------------------------------
// The wizard itself (exported for direct testing)
// ---------------------------------------------------------------------------

export function OnboardingWizard({ onClose }: { onClose: () => void }) {
  const [step, setStep] = useState(0);
  const [draft, setDraft] = useState<OnboardingDraft>(emptyDraft);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<ActivationResult | null>(null);

  const lastStep = ONBOARDING_STEPS.length - 1;
  const progress = useMemo(
    () => Math.round(((step + 1) / ONBOARDING_STEPS.length) * 100),
    [step],
  );

  const onPatch = (patch: Partial<OnboardingDraft>) => {
    setDraft((d) => ({ ...d, ...patch }));
    setError(null);
  };

  const next = () => {
    const err = validateStep(step, draft);
    if (err) {
      setError(err);
      return;
    }
    setError(null);
    setStep((s) => Math.min(s + 1, lastStep));
  };

  const back = () => {
    setError(null);
    setStep((s) => Math.max(0, s - 1));
  };

  const skip = async () => {
    await markOnboardingCompleted('skipped'); // flag only — never a data write
    onClose();
  };

  const activate = async () => {
    if (busy || result) return;
    setBusy(true);
    setError(null);
    try {
      setResult(await activateOnboarding(draft));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Activation failed — try again.');
    } finally {
      setBusy(false);
    }
  };

  // Enter advances from any text input (never from textareas/buttons —
  // multi-line fields and explicit clicks keep their native behavior).
  const onKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter') return;
    const tag = (e.target as HTMLElement).tagName;
    if (tag !== 'INPUT' && tag !== 'SELECT') return;
    e.preventDefault();
    if (step < lastStep) next();
  };

  return (
    <div className="onb-backdrop" role="dialog" aria-modal="true" aria-label="Welcome to Argo OS">
      <GlassPanel variant="hero" className="onb-panel" animate>
        <div className="onb-head">
          <span className="t-eyebrow">Welcome to Argo OS</span>
          <nav className="onb-pills" aria-label="Setup steps">
            {ONBOARDING_STEPS.map((s, i) => (
              <button
                key={s.key}
                type="button"
                className="onb-pill"
                data-state={i === step ? 'active' : i < step ? 'done' : 'todo'}
                // Only completed steps are clickable — no skipping ahead
                // past validation.
                disabled={i >= step || Boolean(result)}
                onClick={() => {
                  setError(null);
                  setStep(i);
                }}
              >
                {s.label}
              </button>
            ))}
          </nav>
          <div className="onb-progress" aria-hidden="true">
            <div className="onb-progress__fill" style={{ width: `${progress}%` }} />
          </div>
        </div>

        <div className="onb-body" onKeyDown={onKeyDown}>
          {step === 0 && <StepCompany draft={draft} onPatch={onPatch} />}
          {step === 1 && <StepModules draft={draft} onPatch={onPatch} />}
          {step === 2 && <StepQuality draft={draft} onPatch={onPatch} />}
          {step === 3 && <StepPersonas draft={draft} onPatch={onPatch} />}
          {step === 4 && <StepTeam draft={draft} onPatch={onPatch} />}
          {step === 5 && <StepGoals draft={draft} onPatch={onPatch} />}
          {step === 6 && <StepReview draft={draft} />}
          {step === 7 && (
            <StepActivate
              companyName={draft.company.name.trim()}
              busy={busy}
              result={result}
              onActivate={() => void activate()}
              onDone={onClose}
            />
          )}
          {error && (
            <div className="login-banner onb-error" role="alert">
              {error}
            </div>
          )}
        </div>

        <div className="onb-foot">
          {!result ? (
            <button type="button" className="onb-skip" onClick={() => void skip()}>
              Skip for now
            </button>
          ) : (
            <span />
          )}
          <div className="onb-foot__actions">
            {step > 0 && !result && (
              <button type="button" className="glass-btn" onClick={back}>
                Back
              </button>
            )}
            {step < lastStep && (
              <button
                type="button"
                className="glass-btn glass-btn--primary"
                onClick={next}
              >
                {step === lastStep - 1 ? 'Continue to activation' : 'Continue'}
              </button>
            )}
          </div>
        </div>
      </GlassPanel>
    </div>
  );
}
